import classes from '../../../Schreibtisch.module.css';
import React, { useEffect, useState } from 'react';
import {
  Accordion,
  Button, Divider,
  Spacer,
  Stack
} from '@efast_public/fjd-component-library';
import VermerkModal from './VermerkModal';
import { VermerkDto } from '../../../../../api/models/Vermerk/VermerkDto';
import { CaseDetailsModel } from '../../../../../api/models/Case/CaseDetailsModel';
import { Bearbeitungsstand } from '../../../../../api/models/Status/Bearbeitungsstand';
import VermerkVerlauf from './VermerkVerlauf';
import DeleteVermerkConfirmationModal from '../../../../shared/DeleteVermerkConfirmationModal';
import { useMutation } from '@tanstack/react-query';
import { BehApiRoutes, behClient } from '../../../../../api/beh-api';
import { useNotification } from '../../../../../shared/notification/useNotification';
import {
  conflictErrorMsg,
  ErrorType
} from '../../../../../shared/utils/Error/ErrorCode';

export interface VermerkProps {
  readonly caseDetails: CaseDetailsModel;
  readonly onStateChanged: () => void;
}

const VermerkData = ({ caseDetails, onStateChanged }: VermerkProps) => {
  const { notifyUserFromRequest, notifyUser } = useNotification();

  const { mutateAsync: createVermerk, isPending: isCreating } = useMutation({
    mutationFn: ({
      vermerk,
      caseId
    }: {
      vermerk: VermerkDto;
      caseId: string;
    }) =>
      notifyUserFromRequest(
        () => behClient.post(BehApiRoutes.vermerkByCaseId(caseId), vermerk),
        {
          successMessage: 'Der Vermerk wurde erfolgreich erstellt.',
          loadingMessage: 'Der Vermerk wird erstellt.',
          errorMessage: 'Der Vermerk konnte nicht erstellt werden.'
        }
      )
  });
  const { mutateAsync: updateVermerk, isPending: isUpdating } = useMutation({
    mutationFn: ({
      vermerk,
      caseId
    }: {
      vermerk: VermerkDto;
      caseId: string;
    }) =>
      notifyUserFromRequest(
        () =>
          behClient.put(
            BehApiRoutes.vermerkByIdAndCaseId(caseId, vermerk.vermerkId!),
            vermerk
          ),
        {
          successMessage: 'Der Vermerk wurde erfolgreich aktualisiert.',
          loadingMessage: 'Der Vermerk wird aktualisiert.',
          errorMessage: 'Der Vermerk konnte nicht aktualisiert werden.',
          errorIdHandler: {
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_UPDATE_NOT_ALLOWED]: () => {
              notifyUser({
                intent: 'error',
                title: conflictErrorMsg
              });
            }
          }
        }
      )
  });
  const { mutateAsync: deleteVermerk, isPending: isDeleting } = useMutation({
    mutationFn: ({
      vermerk,
      caseId
    }: {
      vermerk: VermerkDto;
      caseId: string;
    }) =>
      notifyUserFromRequest(
        () =>
          behClient.delete(
            BehApiRoutes.vermerkByIdAndCaseId(caseId, vermerk.vermerkId!)
          ),
        {
          successMessage: 'Der Vermerk wurde erfolgreich gelöscht.',
          loadingMessage: 'Der Vermerk wird gelöscht.',
          errorMessage: 'Der Vermerk konnte nicht gelöscht werden.'
        }
      )
  });
  const [openVermerkModal, setOpenVermerkModal] = useState(false);
  const [openDeleteConfirmationModal, setOpenDeleteConfirmationModal] =
    useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [vermerkBeEdited, setVermerkBeEdited] = useState<VermerkDto>();
  const [vermerkBeDeleted, setVermerkBeDeleted] = useState<VermerkDto>();
  const [vermerk, setVermerk] = useState<Array<VermerkDto> | undefined>(
    undefined
  );
  const [openCard, setOpenCard] = useState(false);
  const bereitsAbgeschlossen =
    caseDetails.bearbeitungsstand === Bearbeitungsstand.ABGESCHLOSSEN;

  const vermerkDetails: Array<VermerkDto> | undefined | null =
    caseDetails.vermerkDto;

  const vermerkAvailable =
    caseDetails.vermerkDto && caseDetails.vermerkDto.length > 0;

  const caseId = caseDetails.caseId;

  const hasVermerk = vermerk && vermerk?.length > 0;

  useEffect(() => {
    if (vermerkAvailable) {
      setVermerk(vermerkDetails ? vermerkDetails : undefined);
      setOpenCard(true);
    } else {
      setVermerk(undefined);
    }
  }, [vermerkAvailable, vermerkDetails]);

  function handleChildClick(e: React.MouseEvent) {
    setCreateMode(true);
    e.stopPropagation();
    setOpenVermerkModal(true);
  }

  function onEditVermerk(vermerkData: VermerkDto) {
    setCreateMode(false);
    setVermerkBeEdited(vermerkData);
    setOpenVermerkModal(true);
  }

  function onDeleteVermerk(vermerkData: VermerkDto) {
    setVermerkBeDeleted(vermerkData);
    setOpenDeleteConfirmationModal(true);
  }

  return (
    <Stack direction="column">
      <Accordion
        expanded={openCard}
        onChange={() => setOpenCard(!openCard)}
        label={
          <Stack direction="row" spacing="xs">
            <h2 style={{ marginTop: '3px', marginLeft: '10px' }}>Vermerk</h2>
            <h2
              id={'vermerk_length'}
              style={{
                marginLeft: '-5px',
                marginTop: '3px',
                fontWeight: 'normal'
              }}
            >
              ({hasVermerk ? vermerk?.length : '0'})
            </h2>
          </Stack>
        }
        after={
          <Button
            disabled={bereitsAbgeschlossen}
            id="add-vermerk"
            variant="primary-text"
            beforeIcon="add-outline"
            size="sm"
            onClick={(e) => handleChildClick(e)}
          >
            Vermerk hinzufügen
          </Button>
        }
      >
        <div
          style={{ marginLeft: '10px', marginRight: '10px' }}
          className={classes.collapsibleCardBody}
        >
          {hasVermerk ? (
            vermerkDetails?.map((vermerkData, index) => {
              return (
                <Stack direction="column" key={Math.random()} spacing="xs">
                  <VermerkVerlauf
                    vermerk={vermerkData}
                    caseId={caseId}
                    hasBereitsAbgeschlossen={bereitsAbgeschlossen}
                    onEditVermerk={onEditVermerk}
                    onDeleteVermerk={onDeleteVermerk}
                  />
                  {vermerkDetails?.length !== index + 1 && <Divider/>}
                </Stack>
              );
            })
          ) : (
            <div>Es liegt kein Vermerk vor.</div>
          )}
        </div>
      </Accordion>
      {openVermerkModal && (
        <VermerkModal
          onClose={() => setOpenVermerkModal(false)}
          onSave={(vermerk) => {
            return createMode
              ? createVermerk({ vermerk, caseId })
              : updateVermerk({ vermerk, caseId });
          }}
          onStateChanged={onStateChanged}
          disableInput={isCreating || isUpdating || isDeleting}
          vermerk={createMode ? undefined : vermerkBeEdited}
          isOpen
        />
      )}
      <DeleteVermerkConfirmationModal
        onClose={() => setOpenDeleteConfirmationModal(false)}
        onDelete={(vermerk: VermerkDto) =>
          deleteVermerk({ vermerk, caseId }).finally(() => {
            setOpenDeleteConfirmationModal(false);
            onStateChanged();
          })
        }
        vermerk={vermerkBeDeleted ?? ({} as VermerkDto)}
        isOpen={openDeleteConfirmationModal}
      />
    </Stack>
  );
};
export default VermerkData;
